import type { CartAddon, CartItem } from '../types/cart';

function hashString(value: string): string {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return (hash >>> 0).toString(16);
}

function normalizeAddons(addons: CartAddon[] | undefined): CartAddon[] {
  if (!addons?.length) return [];
  return [...addons]
    .map((addon) => ({ id: addon.id, quantity: addon.quantity ?? 1 }))
    .sort((a, b) => a.id.localeCompare(b.id));
}

function normalizeCustomFields(fields: Record<string, string> | undefined): Record<string, string> {
  if (!fields) return {};
  const entries = Object.entries(fields)
    .filter(([, value]) => typeof value === 'string')
    .sort(([a], [b]) => a.localeCompare(b));
  return Object.fromEntries(entries);
}

export type CartLineIdentityInput = Pick<
  CartItem,
  'product_id' | 'variant_id' | 'price_variant_id' | 'addons' | 'custom_fields' | 'price_data' | 'pay_what_you_want_price'
>;

export function computeCartLineId(input: CartLineIdentityInput): string {
  const payload = {
    product_id: input.product_id,
    variant_id: input.variant_id,
    price_variant_id: input.price_variant_id ?? null,
    addons: normalizeAddons(input.addons),
    custom_fields: normalizeCustomFields(input.custom_fields),
    unit_price:
      typeof input.price_data?.unit_price === 'number' && Number.isFinite(input.price_data.unit_price)
        ? input.price_data.unit_price
        : null,
    pay_what_you_want_price:
      typeof input.pay_what_you_want_price === 'number' && Number.isFinite(input.pay_what_you_want_price)
        ? input.pay_what_you_want_price
        : null,
  };
  return hashString(JSON.stringify(payload));
}

export function ensureCartLineId(item: CartItem): CartItem {
  if (typeof item.line_id === 'string' && item.line_id.trim()) {
    return item;
  }
  return { ...item, line_id: computeCartLineId(item) };
}
